import {useEffect, useCallback} from "react"
import LocationPicker from "../LocationPicker"
import PayeeOrderSummary from "../PayeeOrderSummary"
import OrderingFulfillmentDetails from "../OrderingFulfillmentDetails"
import OrderingFulfillmentOptions from "../OrderingFulfillmentOptions"
import DeliveryUnavailableBadge from "../DeliveryUnavailableBadge"
import { FULFILLMENT_METHOD_DELIVERY, FULFILLMENT_METHOD_PICKUP } from "../../constants/payeeOrder"
import { PAYEE_CATALOG_TYPE_ORDERING } from "../../constants/payeeCatalog"
import { useDispatch, useSelector } from "react-redux"
import * as actions from "../../actions"
import styles from "./OrderingCheckout.module.css"
import { alphabeticalSort, capitalize } from "../../utils/stringUtils"
import { getPayeeOrderFeesList } from "../../utils/payeeOrderUtils"
import debounce from 'lodash.debounce'

const OrderingCheckout = ({catalogConfig={}, cartId="", payeeSenderFee=0}) => {
    //the fulfillment options available in the catalog settings
    let {fulfillment={}, location:pickupLocation={}, prepTimes={}, deliveryCatalogsById={}} = catalogConfig || {}
    fulfillment = typeof fulfillment === "object" ? fulfillment : {}
    //whether any of the configured fulfillment methods are delivery methods 
    const hasDeliveryOptions = Boolean (
        fulfillment.delivery &&  
        Object.keys(deliveryCatalogsById).length > 0
    )
    //get the cart values 
    const cart = useSelector(state => state.carts.cartsById[cartId] || {})
    const {catalogMetadata={}, lineItemsById={}} = cart 
    const {deliveryCatalogId="", fulfillmentMethod="", deliveryAddress=null, deliveryAvailabilityId="", deliveryAvailability=null} = catalogMetadata
    //the availabilty responses returned by the delivery availability api for the selected delivery catalog
    const deliveryAvailabilityByPayeeCatalogId = useSelector(state => state.payeeCatalogs.deliveryAvailabilityByPayeeCatalogId)
    const deliveryAvailabilityById = deliveryAvailabilityByPayeeCatalogId[deliveryCatalogId] || {}
    //change delivery catalog id and fulfillment method when the options in the OrderingFulfillmentOptions are selected
    const dispatch = useDispatch()
    const handleChangeDeliveryCatalogId = newDeliveryCatalogId => {
        //check for an existing delivery availability record
        const newDeliveryAvailabilityById = deliveryAvailabilityByPayeeCatalogId[newDeliveryCatalogId] || {}
        const newDeliveryAvailability = newDeliveryAvailabilityById[deliveryAvailabilityId] || null
        //set the new fultillment method
        dispatch(actions.addCatalogMetadataToCart(
            {
                deliveryCatalogId: newDeliveryCatalogId,
                fulfillmentMethod: newDeliveryCatalogId === FULFILLMENT_METHOD_PICKUP ? FULFILLMENT_METHOD_PICKUP : FULFILLMENT_METHOD_DELIVERY,
                pickupAddress: pickupLocation,
                deliveryAvailability: newDeliveryCatalogId === FULFILLMENT_METHOD_PICKUP ? null : newDeliveryAvailability
            }, 
            cartId
        ))
    }
    //on mount, default to delivery if no fulfillment method is selected and it is available
    useEffect(() => {
        //if there is no delivery method selected in the cart, then default to the first delivery option if available
        //otherwise set it to pickup
        if (!deliveryCatalogId){
            const firstDeliveryOption = hasDeliveryOptions ? Object.values(deliveryCatalogsById)
                                              .sort((dA, dB) => alphabeticalSort(dA.payeeName, dB.payeeName))[0]
                                              :
                                              null
            const newDeliveryCatalogId = firstDeliveryOption ? firstDeliveryOption.id                                            :
                                            FULFILLMENT_METHOD_PICKUP
            handleChangeDeliveryCatalogId(newDeliveryCatalogId)
        }
    }, [])
    //save the location to the cart metadata when it is updated
    const pickupSettlementId = pickupLocation ? pickupLocation.settlementId : ""
    const handleChangeDeliveryAddress = deliveryAddress => {
        //area availability id is deterministically generated
        //as `${fromSettlementId}>${toSettlementId}`
        const deliveryAvailabilityId = deliveryAddress && pickupSettlementId? 
                                        `${pickupSettlementId}>${deliveryAddress.settlementId}`
                                        : ""
        dispatch(actions.addCatalogMetadataToCart({
            deliveryAddress, 
            deliveryAvailabilityId, 
            //if the availability to that area has already been loaded, use it, otherwise set to null until loading occurs
            deliveryAvailability: deliveryAvailabilityById[deliveryAvailabilityId] || null
        }, cartId))
    }
    
    const deliverySettlementId = deliveryAddress ? deliveryAddress.settlementId : ""
    //anytime the from and to settlements change, check the availability of all of the 
    //configured delivery options
    const debouncedCheckAvailability = useCallback(
        debounce((pickupId, deliveryId) => {
            Object.keys(deliveryCatalogsById).forEach(deliveryCatalogId => {
                dispatch(
                    actions.callCheckDeliveryCatalogAvailabilityAndPrice(
                        deliveryCatalogId,
                        cartId,
                        pickupId,
                        deliveryId
                    )
                )
            })
        }, 500), // adjust delay (ms) as needed
        [dispatch, deliveryCatalogsById] // dependencies for stable debounce
    )
    useEffect(() => {
        if (
            pickupSettlementId &&
            deliverySettlementId &&
            hasDeliveryOptions
        ){
            debouncedCheckAvailability(pickupSettlementId, deliverySettlementId)
        }
    }, [pickupSettlementId, deliverySettlementId, hasDeliveryOptions, debouncedCheckAvailability]) 
    useEffect(() => {
        return () => {
            debouncedCheckAvailability.cancel()
        }
    }, [debouncedCheckAvailability])

    //display the fees for the order summary
    const feeList = getPayeeOrderFeesList(cart, PAYEE_CATALOG_TYPE_ORDERING, payeeSenderFee)
    return (
        <div className={styles.container}>
            <div className={styles.main}>
                <div className="h3 margin-bottom-05em">How would you like to get your items?</div>
                <OrderingFulfillmentOptions
                    value={deliveryCatalogId}
                    fulfillmentSettings={fulfillment}
                    onChange={handleChangeDeliveryCatalogId}
                    deliveryCatalogsById={deliveryCatalogsById}
                    deliveryAvailabilityId={deliveryAvailabilityId}
                    deliverySettlementId={deliverySettlementId}
                    prepTimes={prepTimes}
                />
                <div className="h3 margin-bottom-05em margin-top-1em">{capitalize(fulfillmentMethod)} address</div>
                {
                    fulfillmentMethod === FULFILLMENT_METHOD_PICKUP?
                    <OrderingFulfillmentDetails 
                        location={pickupLocation}
                        waitTimes={prepTimes}
                        fulfillmentMethod={fulfillmentMethod}
                    />
                    :
                    <LocationPicker 
                        onChange={handleChangeDeliveryAddress}
                        defaultLocation={deliveryAddress}
                        errorMessage={
                            !deliveryAvailability ? //handle case when availablity is not yet loaded
                            <div></div>
                            :
                            !deliveryAvailability.available ?
                            <DeliveryUnavailableBadge 
                                deliveryAvailability={deliveryAvailability}
                                countryId={pickupLocation ? pickupLocation.countryId : ""}
                            />
                            :
                            null
                        }
                    />
                }
            </div>
            <div className={styles.orderBreakdown}>
                <div className={`${styles.summaryTitle} h3 margin-bottom-05em margin-top-1em`}>Order summary</div>
                <PayeeOrderSummary
                    lineItemsById={lineItemsById}
                    feeList={feeList}
                />
            </div>
        </div>
    )
}

export default OrderingCheckout